import {
  PURCHASE_PROCESS_NAME,
  BOOKING_PROCESS_NAME,
  INQUIRY_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  getTransactionProcess,
  getTransactionProcessName,
} from '../transactions/transaction';
import { toCanonicalTransitionName } from '../transactions/transitionAliases';
import { getShipByInfo, getShipByLabel } from './orderStatusGroups';
import { transitions as purchaseTransitions } from '../transactions/transactionProcessPurchase';
import {
  FRONTEND_CANCELED_STATUS_LABEL,
  isFrontendCanceledTransaction,
  isLegacyPayPalReceivedTransaction,
} from './transactionProductionOverrides';

export const CANONICAL_STATUSES = {
  INQUIRY: 'inquiry',
  FREE_INQUIRY: 'freeInquiry',
  QUOTE_REQUESTED: 'quoteRequested',
  REQUEST_REJECTED: 'requestRejected',
  PENDING_PAYMENT: 'pendingPayment',
  PAYMENT_EXPIRED: 'paymentExpired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  SHIPPED: 'shipped',
  PRODUCT_RECEIVED: 'productReceived',
  PRODUCT_RETURN_REQUEST: 'productReturnRequest',
  PRODUCT_RETURN_RECEIVED: 'productReturnReceived',
  DISPUTED: 'disputed',
  WAITING_REVIEW: 'waitingReview',
  COMPLETED: 'completed',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  CHANGES_REQUESTED: 'changesRequested',
  OFFER_REJECTED: 'offerRejected',
  UPDATE_PENDING: 'updatePending',
  OFFER_PENDING: 'offerPending',
  OFFER_COUNTERED: 'offerCountered',
  OFFER_ACCEPTED: 'offerAccepted',
  OFFER_DECLINED: 'offerDeclined',
  OFFER_EXPIRED: 'offerExpired',
  OFFER_PAYMENT_EXPIRED: 'offerPaymentExpired',
  UNKNOWN: 'unknown',
};

export const STATUS_BADGE_VARIANTS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  DELIVERED: 'delivered',
  COMPLETE: 'complete',
  CANCELLED: 'cancelled',
  ERROR: 'error',
  PRODUCT_RECEIVED: 'productReceived',
  PRODUCT_RETURN_REQUEST: 'productReturnRequest',
  PRODUCT_RETURN_RECEIVED: 'productReturnReceived',
  NEUTRAL: 'neutral',
};

const OFFER_RESPONSE_WINDOW_MS = 24 * 60 * 60 * 1000;
const OFFER_PAYMENT_WINDOW_MS = 24 * 60 * 60 * 1000;

const OFFER_ACCEPTED_TRANSITIONS = [
  purchaseTransitions.TRANSITION_ACCEPT_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER,
];

const OFFER_DECLINED_TRANSITIONS = [
  purchaseTransitions.TRANSITION_DECLINE_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
];

const LEGACY_STATE_ALIASES = {
  enquiry: 'inquiry',
  'pending-paypal-payment': 'pending-payment',
  'custom-pending-payment': 'pending-payment',
  'pending-payment-without-payout': 'pending-payment',
  'payment-with-paypal-expired': 'payment-expired',
  'custom-payment-expired': 'payment-expired',
  'payment-expired-without-payout': 'payment-expired',
  'purchased-with-paypal': 'purchased',
  'custom-purchased': 'purchased',
  'purchased-without-payout': 'purchased',
  'delivered-with-paypal': 'delivered',
  'custom-delivered': 'delivered',
  'delivered-without-payout': 'delivered',
  'received-without-payout': 'received',
  'disputed-with-paypal': 'disputed',
  'custom-disputed': 'disputed',
  'disputed-without-payout': 'disputed',
  'canceled-with-paypal': 'canceled',
  'update-wallet': 'received',
  'pending-shipping': 'purchased',
  'product-return-received': 'product-return-request-received',
  'offer-auto-eligible-pending': 'offer-pending',
};

const PURCHASE_STATE_TO_CANONICAL = {
  inquiry: CANONICAL_STATUSES.INQUIRY,
  'pending-payment': CANONICAL_STATUSES.PENDING_PAYMENT,
  'payment-expired': CANONICAL_STATUSES.PAYMENT_EXPIRED,
  purchased: CANONICAL_STATUSES.PURCHASED,
  delivered: CANONICAL_STATUSES.DELIVERED,
  shipped: CANONICAL_STATUSES.SHIPPED,
  'product-received': CANONICAL_STATUSES.PRODUCT_RECEIVED,
  'product-return-request': CANONICAL_STATUSES.PRODUCT_RETURN_REQUEST,
  'product-return-request-received': CANONICAL_STATUSES.PRODUCT_RETURN_RECEIVED,
  disputed: CANONICAL_STATUSES.DISPUTED,
  received: CANONICAL_STATUSES.WAITING_REVIEW,
  completed: CANONICAL_STATUSES.WAITING_REVIEW,
  'reviewed-by-customer': CANONICAL_STATUSES.COMPLETED,
  'reviewed-by-provider': CANONICAL_STATUSES.COMPLETED,
  reviewed: CANONICAL_STATUSES.COMPLETED,
  canceled: CANONICAL_STATUSES.CANCELED,
  'offer-pending': CANONICAL_STATUSES.OFFER_PENDING,
  'offer-countered': CANONICAL_STATUSES.OFFER_COUNTERED,
  'offer-accepted': CANONICAL_STATUSES.OFFER_ACCEPTED,
  'offer-declined': CANONICAL_STATUSES.OFFER_DECLINED,
  'offer-payment-expired': CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED,
};

const BOOKING_STATE_TO_CANONICAL = {
  inquiry: CANONICAL_STATUSES.INQUIRY,
  'pending-payment': CANONICAL_STATUSES.PENDING_PAYMENT,
  'payment-expired': CANONICAL_STATUSES.PAYMENT_EXPIRED,
  preauthorized: CANONICAL_STATUSES.PENDING_PAYMENT,
  accepted: CANONICAL_STATUSES.ACCEPTED,
  declined: CANONICAL_STATUSES.DECLINED,
  expired: CANONICAL_STATUSES.EXPIRED,
  canceled: CANONICAL_STATUSES.CANCELED,
  delivered: CANONICAL_STATUSES.WAITING_REVIEW,
  'reviewed-by-customer': CANONICAL_STATUSES.WAITING_REVIEW,
  'reviewed-by-provider': CANONICAL_STATUSES.WAITING_REVIEW,
  reviewed: CANONICAL_STATUSES.COMPLETED,
};

const NEGOTIATION_STATE_TO_CANONICAL = {
  inquiry: CANONICAL_STATUSES.INQUIRY,
  'quote-requested': CANONICAL_STATUSES.QUOTE_REQUESTED,
  'request-rejected': CANONICAL_STATUSES.REQUEST_REJECTED,
  'offer-pending': CANONICAL_STATUSES.OFFER_PENDING,
  'update-pending': CANONICAL_STATUSES.UPDATE_PENDING,
  'customer-offer-pending': CANONICAL_STATUSES.OFFER_COUNTERED,
  'offer-rejected': CANONICAL_STATUSES.OFFER_REJECTED,
  'pending-payment': CANONICAL_STATUSES.PENDING_PAYMENT,
  'payment-expired': CANONICAL_STATUSES.PAYMENT_EXPIRED,
  'offer-accepted': CANONICAL_STATUSES.PURCHASED,
  canceled: CANONICAL_STATUSES.CANCELED,
  delivered: CANONICAL_STATUSES.DELIVERED,
  'changes-requested': CANONICAL_STATUSES.CHANGES_REQUESTED,
  completed: CANONICAL_STATUSES.WAITING_REVIEW,
  'reviewed-by-customer': CANONICAL_STATUSES.WAITING_REVIEW,
  'reviewed-by-provider': CANONICAL_STATUSES.WAITING_REVIEW,
  reviewed: CANONICAL_STATUSES.COMPLETED,
};

const INQUIRY_STATE_TO_CANONICAL = {
  'free-inquiry': CANONICAL_STATUSES.FREE_INQUIRY,
};

const TRANSITION_TO_CANONICAL = {
  'transition/inquire': CANONICAL_STATUSES.INQUIRY,
  'transition/inquire-without-payment': CANONICAL_STATUSES.FREE_INQUIRY,
  'transition/request-quote': CANONICAL_STATUSES.QUOTE_REQUESTED,
  'transition/reject-request': CANONICAL_STATUSES.REQUEST_REJECTED,
  'transition/withdraw-request': CANONICAL_STATUSES.REQUEST_REJECTED,
  'transition/operator-reject-request': CANONICAL_STATUSES.REQUEST_REJECTED,
  'transition/request-payment': CANONICAL_STATUSES.PENDING_PAYMENT,
  'transition/request-payment-after-inquiry': CANONICAL_STATUSES.PENDING_PAYMENT,
  'transition/request-payment-to-accept-offer': CANONICAL_STATUSES.PENDING_PAYMENT,
  'transition/request-booking-without-payout': CANONICAL_STATUSES.PENDING_PAYMENT,
  'transition/expire-payment-without-payout': CANONICAL_STATUSES.PAYMENT_EXPIRED,
  'transition/expire-payment': CANONICAL_STATUSES.PAYMENT_EXPIRED,
  'transition/confirm-payment': CANONICAL_STATUSES.PURCHASED,
  'transition/confirm-booking-without-payout': CANONICAL_STATUSES.PURCHASED,
  'transition/accept': CANONICAL_STATUSES.ACCEPTED,
  'transition/operator-accept': CANONICAL_STATUSES.ACCEPTED,
  'transition/decline': CANONICAL_STATUSES.DECLINED,
  'transition/operator-decline': CANONICAL_STATUSES.DECLINED,
  'transition/expire': CANONICAL_STATUSES.EXPIRED,
  'transition/cancel': CANONICAL_STATUSES.CANCELED,
  'transition/auto-cancel': CANONICAL_STATUSES.CANCELED,
  'transition/auto-cancel-from-disputed': CANONICAL_STATUSES.CANCELED,
  'transition/cancel-from-disputed': CANONICAL_STATUSES.CANCELED,
  'transition/operator-cancel': CANONICAL_STATUSES.CANCELED,
  'transition/operator-cancel-from-delivered': CANONICAL_STATUSES.CANCELED,
  'transition/auto-cancel-from-changes-requested': CANONICAL_STATUSES.CANCELED,
  'transition/operator-cancel-from-changes-requested': CANONICAL_STATUSES.CANCELED,
  'transition/mark-delivered': CANONICAL_STATUSES.DELIVERED,
  'transition/operator-mark-delivered': CANONICAL_STATUSES.DELIVERED,
  'transition/deliver': CANONICAL_STATUSES.DELIVERED,
  'transition/deliver-changes': CANONICAL_STATUSES.DELIVERED,
  'transition/operator-mark-changes-delivered': CANONICAL_STATUSES.DELIVERED,
  'transition/product-shipped': CANONICAL_STATUSES.SHIPPED,
  'transition/product-shipped-with-paypal': CANONICAL_STATUSES.SHIPPED,
  'transition/product-received': CANONICAL_STATUSES.PRODUCT_RECEIVED,
  'transition/product-received-with-paypal': CANONICAL_STATUSES.PRODUCT_RECEIVED,
  'transition/return-to-seller': CANONICAL_STATUSES.PRODUCT_RETURN_REQUEST,
  'transition/return-to-seller-with-paypal': CANONICAL_STATUSES.PRODUCT_RETURN_REQUEST,
  'transition/seller-received-return': CANONICAL_STATUSES.PRODUCT_RETURN_RECEIVED,
  'transition/seller-received-return-with-paypal': CANONICAL_STATUSES.PRODUCT_RETURN_RECEIVED,
  'transition/dispute': CANONICAL_STATUSES.DISPUTED,
  'transition/operator-dispute': CANONICAL_STATUSES.DISPUTED,
  'transition/mark-received-from-purchased': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/mark-received': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/auto-mark-received': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/mark-received-from-disputed': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/complete': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/operator-complete': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/accept-deliverable': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/auto-accept-deliverable': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/operator-accept-deliverable': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/auto-complete': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/wallet-updated': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/update-wallet-balance': CANONICAL_STATUSES.WAITING_REVIEW,
  'transition/request-changes': CANONICAL_STATUSES.CHANGES_REQUESTED,
  'transition/operator-request-changes': CANONICAL_STATUSES.CHANGES_REQUESTED,
  'transition/review-1-by-provider': CANONICAL_STATUSES.COMPLETED,
  'transition/review-1-by-customer': CANONICAL_STATUSES.COMPLETED,
  'transition/review-2-by-provider': CANONICAL_STATUSES.COMPLETED,
  'transition/review-2-by-customer': CANONICAL_STATUSES.COMPLETED,
  'transition/expire-customer-review-period': CANONICAL_STATUSES.COMPLETED,
  'transition/expire-provider-review-period': CANONICAL_STATUSES.COMPLETED,
  'transition/expire-review-period': CANONICAL_STATUSES.COMPLETED,
};

const parseDate = value => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeStateName = stateName => {
  if (!stateName || typeof stateName !== 'string') {
    return null;
  }
  const stateWithoutPrefix = stateName.includes('/') ? stateName.split('/').pop() : stateName;
  const normalized = stateWithoutPrefix.trim().toLowerCase();
  return LEGACY_STATE_ALIASES[normalized] || normalized;
};

const normalizeTransitionName = transitionName => {
  if (!transitionName || typeof transitionName !== 'string') {
    return null;
  }
  return toCanonicalTransitionName(transitionName);
};

const normalizeViewerRole = viewerRole => {
  if (viewerRole === 'order') {
    return TX_TRANSITION_ACTOR_CUSTOMER;
  }
  if (viewerRole === 'sale') {
    return TX_TRANSITION_ACTOR_PROVIDER;
  }
  if (viewerRole === TX_TRANSITION_ACTOR_CUSTOMER || viewerRole === TX_TRANSITION_ACTOR_PROVIDER) {
    return viewerRole;
  }
  return TX_TRANSITION_ACTOR_CUSTOMER;
};

const isCustomerViewer = viewerRole =>
  normalizeViewerRole(viewerRole) === TX_TRANSITION_ACTOR_CUSTOMER;

const getProcessStateData = transaction => {
  const processName = getTransactionProcessName(transaction);
  if (!processName) {
    return { processName: null, processState: null };
  }

  try {
    const process = getTransactionProcess(transaction);
    return {
      processName,
      processState: normalizeStateName(process.getState(transaction)),
    };
  } catch {
    return {
      processName,
      processState: null,
    };
  }
};

const getOfferCreatedAt = transaction => {
  const unixSeconds = transaction?.attributes?.protectedData?.offerParams?.createdAt;
  if (typeof unixSeconds === 'number') {
    return new Date(unixSeconds * 1000);
  }
  return parseDate(transaction?.attributes?.createdAt);
};

const getLastTransitionedAt = transaction =>
  parseDate(transaction?.attributes?.lastTransitionedAt) ||
  parseDate(transaction?.attributes?.createdAt);

const getLatestTransitionRecord = (transaction, transitionNames) => {
  const transitions = transaction?.attributes?.transitions || [];
  const matching = transitions.filter(t => {
    const canonicalTransition = normalizeTransitionName(t?.transition);
    return transitionNames.includes(canonicalTransition);
  });
  if (!matching.length) {
    return null;
  }

  return matching.reduce((latest, current) => {
    const latestDate = parseDate(latest?.createdAt)?.getTime() || 0;
    const currentDate = parseDate(current?.createdAt)?.getTime() || 0;
    return currentDate > latestDate ? current : latest;
  }, matching[0]);
};

const getOfferAcceptedAt = transaction => {
  const acceptedTransition = getLatestTransitionRecord(transaction, OFFER_ACCEPTED_TRANSITIONS);
  return parseDate(acceptedTransition?.createdAt) || getLastTransitionedAt(transaction);
};

export const getOfferDeadline = (transaction, _now = new Date()) => {
  const lastTransition = normalizeTransitionName(transaction?.attributes?.lastTransition);

  if (lastTransition === purchaseTransitions.TRANSITION_SEND_OFFER) {
    const createdAt = getOfferCreatedAt(transaction);
    return createdAt ? new Date(createdAt.getTime() + OFFER_RESPONSE_WINDOW_MS) : null;
  }

  if (lastTransition === purchaseTransitions.TRANSITION_COUNTER_OFFER) {
    const counteredAt = getLastTransitionedAt(transaction);
    return counteredAt ? new Date(counteredAt.getTime() + OFFER_RESPONSE_WINDOW_MS) : null;
  }

  if (OFFER_ACCEPTED_TRANSITIONS.includes(lastTransition)) {
    const acceptedAt = getOfferAcceptedAt(transaction);
    return acceptedAt ? new Date(acceptedAt.getTime() + OFFER_PAYMENT_WINDOW_MS) : null;
  }

  return null;
};

export const getOfferTimeRemainingMs = (transaction, now = new Date()) => {
  const deadline = getOfferDeadline(transaction, now);
  if (!deadline) {
    return null;
  }
  const nowDate = now instanceof Date ? now : new Date(now);
  return deadline.getTime() - nowDate.getTime();
};

const getCanonicalOfferStatus = (transaction, now = new Date()) => {
  const lastTransition = normalizeTransitionName(transaction?.attributes?.lastTransition);
  const remainingTime = getOfferTimeRemainingMs(transaction, now);
  const hasExpired = typeof remainingTime === 'number' && remainingTime <= 0;

  if (!lastTransition) {
    return null;
  }

  if (lastTransition === purchaseTransitions.TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT) {
    return CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED;
  }

  if (OFFER_DECLINED_TRANSITIONS.includes(lastTransition)) {
    return CANONICAL_STATUSES.OFFER_DECLINED;
  }

  if (OFFER_ACCEPTED_TRANSITIONS.includes(lastTransition)) {
    return hasExpired
      ? CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED
      : CANONICAL_STATUSES.OFFER_ACCEPTED;
  }

  if (lastTransition === purchaseTransitions.TRANSITION_COUNTER_OFFER) {
    return hasExpired ? CANONICAL_STATUSES.OFFER_EXPIRED : CANONICAL_STATUSES.OFFER_COUNTERED;
  }

  if (lastTransition === purchaseTransitions.TRANSITION_SEND_OFFER) {
    return hasExpired ? CANONICAL_STATUSES.OFFER_EXPIRED : CANONICAL_STATUSES.OFFER_PENDING;
  }

  return null;
};

const getCanonicalStatusFromPurchaseState = (processState, viewerRole) => {
  const customerViewer = isCustomerViewer(viewerRole);

  switch (processState) {
    case 'received':
    case 'completed':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    case 'reviewed-by-provider':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    default:
      return PURCHASE_STATE_TO_CANONICAL[processState] || null;
  }
};

const getCanonicalStatusFromBookingState = (processState, viewerRole) => {
  const customerViewer = isCustomerViewer(viewerRole);

  switch (processState) {
    case 'delivered':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    case 'reviewed-by-provider':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    case 'reviewed-by-customer':
      return CANONICAL_STATUSES.COMPLETED;
    default:
      return BOOKING_STATE_TO_CANONICAL[processState] || null;
  }
};

const getCanonicalStatusFromNegotiationState = (processState, viewerRole) => {
  const customerViewer = isCustomerViewer(viewerRole);

  switch (processState) {
    case 'completed':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    case 'reviewed-by-provider':
      return customerViewer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED;
    case 'reviewed-by-customer':
      return CANONICAL_STATUSES.COMPLETED;
    default:
      return NEGOTIATION_STATE_TO_CANONICAL[processState] || null;
  }
};

const getCanonicalStatusFromProcessState = (processName, processState, viewerRole) => {
  if (!processName || !processState) {
    return null;
  }

  if (processName === PURCHASE_PROCESS_NAME) {
    return getCanonicalStatusFromPurchaseState(processState, viewerRole);
  }
  if (processName === BOOKING_PROCESS_NAME) {
    return getCanonicalStatusFromBookingState(processState, viewerRole);
  }
  if (processName === NEGOTIATION_PROCESS_NAME) {
    return getCanonicalStatusFromNegotiationState(processState, viewerRole);
  }
  if (processName === INQUIRY_PROCESS_NAME) {
    return INQUIRY_STATE_TO_CANONICAL[processState] || null;
  }
  return null;
};

export const getCanonicalStatus = (transaction, viewerRole, options = {}) => {
  // Temporary frontend-only fallback for one production transaction. This affects display
  // status only and must not be used to perform backend transitions or metadata updates.
  if (isFrontendCanceledTransaction(transaction)) {
    return CANONICAL_STATUSES.CANCELED;
  }

  if (isLegacyPayPalReceivedTransaction(transaction)) {
    return isCustomerViewer(viewerRole)
      ? CANONICAL_STATUSES.WAITING_REVIEW
      : CANONICAL_STATUSES.COMPLETED;
  }

  const now = options?.now || new Date();
  const { processName, processState } = getProcessStateData(transaction);
  const normalizedTransition = normalizeTransitionName(transaction?.attributes?.lastTransition);
  const normalizedStateFromTx = normalizeStateName(transaction?.attributes?.state);

  const offerStatus = getCanonicalOfferStatus(transaction, now);
  if (offerStatus) {
    return offerStatus;
  }

  const byProcessState = getCanonicalStatusFromProcessState(
    processName,
    processState || normalizedStateFromTx,
    viewerRole
  );
  if (byProcessState) {
    return byProcessState;
  }

  if (normalizedTransition && TRANSITION_TO_CANONICAL[normalizedTransition]) {
    return TRANSITION_TO_CANONICAL[normalizedTransition];
  }

  if (normalizedStateFromTx && PURCHASE_STATE_TO_CANONICAL[normalizedStateFromTx]) {
    return PURCHASE_STATE_TO_CANONICAL[normalizedStateFromTx];
  }

  return CANONICAL_STATUSES.UNKNOWN;
};

const getPurchaseStateLabel = (state, viewerRole) => {
  const isCustomer = isCustomerViewer(viewerRole);
  switch (state) {
    case 'inquiry':
      return 'Enquiry';
    case 'pending-payment':
      return 'Awaiting payment';
    case 'payment-expired':
      return 'Payment not completed';
    case 'purchased':
      // Sellers see the ship-by date (getStatusLabel adds it); buyers see it being prepared
      return isCustomer ? 'Preparing' : 'Ship now';
    case 'delivered':
    case 'shipped':
      return 'Shipped';
    case 'product-received':
      return 'Product Received';
    case 'product-return-request':
      return 'Return Request';
    case 'product-return-request-received':
      return 'Product Returned';
    case 'disputed':
      return 'Disputed';
    case 'received':
      return 'Delivered';
    case 'completed':
    case 'reviewed-by-customer':
    case 'reviewed-by-provider':
    case 'reviewed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    case 'offer-pending':
      return isCustomer ? 'Offer pending' : 'Offer received';
    case 'offer-countered':
      return 'Counter offer';
    case 'offer-accepted':
      return 'Offer accepted';
    case 'offer-declined':
      return 'Offer declined';
    case 'offer-payment-expired':
      return 'Offer has expired';
    default:
      return null;
  }
};

const getBookingStateLabel = (state, viewerRole) => {
  const isCustomer = isCustomerViewer(viewerRole);
  switch (state) {
    case 'inquiry':
      return 'Inquiry';
    case 'pending-payment':
      return 'Pending payment';
    case 'payment-expired':
      return 'Payment expired';
    case 'preauthorized':
      return 'Requested';
    case 'accepted':
      return 'Accepted';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    case 'canceled':
      return 'Canceled';
    case 'delivered':
      return isCustomer ? 'Waiting for review' : 'Completed';
    case 'reviewed-by-customer':
      return 'Completed';
    case 'reviewed-by-provider':
      return isCustomer ? 'Waiting for your review' : 'Completed';
    case 'reviewed':
      return 'Completed';
    default:
      return null;
  }
};

const getNegotiationStateLabel = (state, viewerRole) => {
  const isCustomer = isCustomerViewer(viewerRole);
  switch (state) {
    case 'inquiry':
      return 'Inquiry';
    case 'quote-requested':
      return 'Quote requested';
    case 'request-rejected':
      return 'Request rejected';
    case 'offer-pending':
      return isCustomer ? 'Offer pending' : 'Offer received';
    case 'update-pending':
      return isCustomer ? 'Offer update pending' : 'Offer updated';
    case 'customer-offer-pending':
      return 'Counter offer';
    case 'offer-rejected':
      return 'Rejected';
    case 'pending-payment':
      return 'Pending payment';
    case 'payment-expired':
      return 'Payment expired';
    case 'offer-accepted':
      return 'Waiting for delivery';
    case 'canceled':
      return 'Canceled';
    case 'delivered':
      return isCustomer ? 'Accept order' : 'Delivered';
    case 'changes-requested':
      return 'Changes requested';
    case 'completed':
      return isCustomer ? 'Waiting for review' : 'Completed';
    case 'reviewed-by-customer':
      return 'Completed';
    case 'reviewed-by-provider':
      return isCustomer ? 'Waiting for your review' : 'Completed';
    case 'reviewed':
      return 'Completed';
    default:
      return null;
  }
};

const getLabelFromCanonicalStatus = (status, viewerRole) => {
  const isCustomer = isCustomerViewer(viewerRole);
  switch (status) {
    case CANONICAL_STATUSES.INQUIRY:
      return 'Inquiry';
    case CANONICAL_STATUSES.FREE_INQUIRY:
      return 'Free inquiry';
    case CANONICAL_STATUSES.QUOTE_REQUESTED:
      return 'Quote requested';
    case CANONICAL_STATUSES.REQUEST_REJECTED:
      return 'Request rejected';
    case CANONICAL_STATUSES.PENDING_PAYMENT:
      return 'Pending payment';
    case CANONICAL_STATUSES.PAYMENT_EXPIRED:
      return 'Payment expired';
    case CANONICAL_STATUSES.PURCHASED:
      return 'Waiting for delivery';
    case CANONICAL_STATUSES.DELIVERED:
      return isCustomer ? 'Mark as received' : 'Delivered';
    case CANONICAL_STATUSES.SHIPPED:
      return 'Shipped';
    case CANONICAL_STATUSES.PRODUCT_RECEIVED:
      return 'Product Received';
    case CANONICAL_STATUSES.PRODUCT_RETURN_REQUEST:
      return 'Return Request';
    case CANONICAL_STATUSES.PRODUCT_RETURN_RECEIVED:
      return 'Product Returned';
    case CANONICAL_STATUSES.DISPUTED:
      return 'Disputed';
    case CANONICAL_STATUSES.WAITING_REVIEW:
      return 'Waiting for review';
    case CANONICAL_STATUSES.COMPLETED:
      return 'Completed';
    case CANONICAL_STATUSES.ACCEPTED:
      return 'Accepted';
    case CANONICAL_STATUSES.DECLINED:
      return 'Declined';
    case CANONICAL_STATUSES.EXPIRED:
      return 'Expired';
    case CANONICAL_STATUSES.CANCELED:
      return 'Canceled';
    case CANONICAL_STATUSES.CHANGES_REQUESTED:
      return 'Changes requested';
    case CANONICAL_STATUSES.OFFER_REJECTED:
      return 'Rejected';
    case CANONICAL_STATUSES.UPDATE_PENDING:
      return isCustomer ? 'Offer update pending' : 'Offer updated';
    case CANONICAL_STATUSES.OFFER_PENDING:
      return isCustomer ? 'Offer pending' : 'Offer received';
    case CANONICAL_STATUSES.OFFER_COUNTERED:
      return 'Counter offer';
    case CANONICAL_STATUSES.OFFER_ACCEPTED:
      return 'Offer accepted';
    case CANONICAL_STATUSES.OFFER_DECLINED:
      return 'Offer declined';
    case CANONICAL_STATUSES.OFFER_EXPIRED:
    case CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED:
      return 'Offer has expired';
    default:
      return 'Not Found';
  }
};

export const getStatusLabel = ({ transaction, viewerRole, intl: _intl, now = new Date() }) => {
  if (isFrontendCanceledTransaction(transaction)) {
    return FRONTEND_CANCELED_STATUS_LABEL;
  }

  if (isLegacyPayPalReceivedTransaction(transaction)) {
    return 'Received';
  }

  const role = normalizeViewerRole(viewerRole);
  const { processName, processState } = getProcessStateData(transaction);
  const normalizedStateFromTx = normalizeStateName(transaction?.attributes?.state);
  const state = processState || normalizedStateFromTx;

  let processStateLabel = null;
  if (processName === PURCHASE_PROCESS_NAME && state === 'purchased' && !isCustomerViewer(role)) {
    processStateLabel = getShipByLabel(transaction, now);
  } else if (processName === PURCHASE_PROCESS_NAME) {
    processStateLabel = getPurchaseStateLabel(state, role);
  } else if (processName === BOOKING_PROCESS_NAME) {
    processStateLabel = getBookingStateLabel(state, role);
  } else if (processName === NEGOTIATION_PROCESS_NAME) {
    processStateLabel = getNegotiationStateLabel(state, role);
  } else if (processName === INQUIRY_PROCESS_NAME && state === 'free-inquiry') {
    processStateLabel = 'Free inquiry';
  }

  // A wallet seller is paid when the order is received, so it reads as completed for them
  if (
    processStateLabel === 'Delivered' &&
    getCanonicalStatus(transaction, role, { now }) === CANONICAL_STATUSES.COMPLETED
  ) {
    return 'Completed';
  }

  if (processStateLabel) {
    return processStateLabel;
  }

  const canonicalStatus = getCanonicalStatus(transaction, role, { now });
  return getLabelFromCanonicalStatus(canonicalStatus, role);
};

export const getStatusBadgeVariant = ({ transaction, viewerRole, now = new Date() }) => {
  const status = getCanonicalStatus(transaction, viewerRole, { now });
  // A seller's order past its ship-by date shows in red
  if (
    status === CANONICAL_STATUSES.PURCHASED &&
    !isCustomerViewer(normalizeViewerRole(viewerRole)) &&
    getShipByInfo(transaction, now)?.isPastShipBy
  ) {
    return STATUS_BADGE_VARIANTS.ERROR;
  }

  switch (status) {
    case CANONICAL_STATUSES.PRODUCT_RECEIVED:
      return STATUS_BADGE_VARIANTS.PRODUCT_RECEIVED;
    case CANONICAL_STATUSES.PRODUCT_RETURN_REQUEST:
      return STATUS_BADGE_VARIANTS.PRODUCT_RETURN_REQUEST;
    case CANONICAL_STATUSES.PRODUCT_RETURN_RECEIVED:
      return STATUS_BADGE_VARIANTS.PRODUCT_RETURN_RECEIVED;
    case CANONICAL_STATUSES.PAYMENT_EXPIRED:
    case CANONICAL_STATUSES.EXPIRED:
    case CANONICAL_STATUSES.OFFER_EXPIRED:
    case CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED:
      return STATUS_BADGE_VARIANTS.ERROR;
    case CANONICAL_STATUSES.CANCELED:
    case CANONICAL_STATUSES.DECLINED:
    case CANONICAL_STATUSES.REQUEST_REJECTED:
    case CANONICAL_STATUSES.OFFER_REJECTED:
    case CANONICAL_STATUSES.OFFER_DECLINED:
      return STATUS_BADGE_VARIANTS.CANCELLED;
    case CANONICAL_STATUSES.DELIVERED:
    case CANONICAL_STATUSES.COMPLETED:
      return STATUS_BADGE_VARIANTS.DELIVERED;
    case CANONICAL_STATUSES.PURCHASED:
    case CANONICAL_STATUSES.SHIPPED:
    case CANONICAL_STATUSES.WAITING_REVIEW:
    case CANONICAL_STATUSES.ACCEPTED:
    case CANONICAL_STATUSES.OFFER_ACCEPTED:
      return STATUS_BADGE_VARIANTS.SUCCESS;
    case CANONICAL_STATUSES.INQUIRY:
    case CANONICAL_STATUSES.FREE_INQUIRY:
    case CANONICAL_STATUSES.QUOTE_REQUESTED:
    case CANONICAL_STATUSES.PENDING_PAYMENT:
    case CANONICAL_STATUSES.DISPUTED:
    case CANONICAL_STATUSES.CHANGES_REQUESTED:
    case CANONICAL_STATUSES.UPDATE_PENDING:
    case CANONICAL_STATUSES.OFFER_PENDING:
    case CANONICAL_STATUSES.OFFER_COUNTERED:
      return STATUS_BADGE_VARIANTS.PENDING;
    default:
      return STATUS_BADGE_VARIANTS.NEUTRAL;
  }
};

export const getStatusInfo = ({ transaction, viewerRole, intl, now = new Date() }) => {
  const canonicalStatus = getCanonicalStatus(transaction, viewerRole, { now });
  return {
    canonicalStatus,
    label: getStatusLabel({ transaction, viewerRole, intl, now }),
    badgeVariant: getStatusBadgeVariant({ transaction, viewerRole, now }),
  };
};
